import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Radio, Calendar, Users, ChevronRight } from "lucide-react";

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

const STATE_STYLES = {
  upcoming: { label: "Upcoming", cls: "bg-brand-blue/10 text-brand-gold border-brand-gold/40" },
  live:     { label: "LIVE NOW",  cls: "bg-red-600 text-white border-red-600 animate-pulse" },
  ended:    { label: "Ended",     cls: "bg-secondary/60 text-muted-foreground border-neutral-300" },
};

export default function LiveTestList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/live-tests")
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-crimson mb-4">Weekly competitions</div>
          <h1 className="text-4xl lg:text-5xl font-black tracking-tight flex items-center gap-4" data-testid="live-test-list-title">
            <Radio className="w-10 h-10 text-brand-crimson" /> Live tests
          </h1>
          <p className="mt-3 font-mono text-muted-foreground max-w-2xl">
            Fixed start time par sab ek saath test dete hain. 15-min me auto-submit. Registration free — trial expired ho to bhi participate kar sakte ho.
          </p>
        </div>

        {loading ? (
          <div className="font-mono text-muted-foreground">Loading…</div>
        ) : items.length === 0 ? (
          <div className="border-2 border-dashed border-border p-12 text-center" data-testid="live-test-empty">
            <Radio className="w-12 h-12 mx-auto text-neutral-300 mb-4" />
            <h3 className="text-xl font-black tracking-tight">No live tests scheduled yet</h3>
            <p className="mt-2 font-mono text-sm text-muted-foreground">Admin will publish upcoming tests here. Check back soon.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="live-test-grid">
            {items.map((t) => {
              const style = STATE_STYLES[t.state] || STATE_STYLES.ended;
              return (
                <Link
                  key={t.id}
                  to={`/live-tests/${t.id}`}
                  data-testid={`live-test-card-${t.id}`}
                  className="group border border-border p-6 flex flex-col hover:border-brand-crimson/40 hover:-translate-y-1 transition-all bg-card"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className={`text-[10px] uppercase tracking-widest font-bold border px-2 py-0.5 ${style.cls}`}>
                      {style.label}
                    </span>
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground/70 font-bold">{t.module}</span>
                  </div>
                  <h3 className="text-2xl font-black tracking-tight">{t.title}</h3>
                  {t.description && <p className="mt-2 font-mono text-sm text-muted-foreground line-clamp-2">{t.description}</p>}
                  <div className="mt-4 flex items-center gap-4 text-xs font-mono text-muted-foreground">
                    <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {fmtDate(t.scheduled_at)}</span>
                    <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {t.registered_count}</span>
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-widest font-bold">
                      {t.has_submitted ? "Submitted" : t.is_registered ? "Registered" : "Open to register"}
                    </span>
                    <ChevronRight className="w-4 h-4 text-brand-crimson" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
